// The enclosure mark — two brackets and the dot inside them — drawn as the
// design system draws it (the brand's `logo-enclosure.svg`: a 160-unit frame,
// brackets at stroke 9, the dot of radius 17 at the centre). Here the dot is
// the connection's live state (02-DESIGN/03-console/01-the-console.md § the
// look): teal while the connection is placed, a slow pulse while it
// reconnects, grey when it is not connected. It is the console's only
// motion.

import type { State } from "../platform/bus.ts";

/** Live is what the dot says. */
export type Live = "placed" | "reconnecting" | "off";

/** liveOf reads the dot's state off the connection's. */
export function liveOf(state: State | undefined): Live {
  if (state === undefined || state.kind !== "connected") {
    return "off";
  }
  return state.reconnecting ? "reconnecting" : "placed";
}

const TITLE: Record<Live, string> = {
  placed: "Connected",
  reconnecting: "Reconnecting",
  off: "Not connected",
};

/**
 * Mark draws the mark. Decorative, it says nothing to a screen reader: on the
 * front page nothing is connecting, so the name beside it is all there is to
 * hear (03-the-signed-out-site.md § the top line).
 */
export function Mark({
  live = "off",
  size = 22,
  decorative = false,
}: {
  live?: Live;
  size?: number;
  decorative?: boolean;
}) {
  const a11y = decorative
    ? ({ "aria-hidden": true } as const)
    : ({ role: "img", "aria-label": TITLE[live] } as const);
  return (
    <svg className="mark" width={size} height={size} viewBox="0 0 160 160" {...a11y} focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="62,48 44,48 44,112 62,112" />
        <polyline points="98,48 116,48 116,112 98,112" />
      </g>
      <circle className={`mark-dot ${live}`} cx="80" cy="80" r="17" />
    </svg>
  );
}

/** Wordmark is the mark beside the name, as the sign-in pages carry it. */
export function Wordmark({ live = "off", decorative = false }: { live?: Live; decorative?: boolean }) {
  return (
    <div className="brand">
      <Mark live={live} size={30} decorative={decorative} />
      <span className="word">impire</span>
    </div>
  );
}
